import { RowData } from '../App';
import type { ApiBlock } from '../utils/apiTypes';

export type SbsBlocksOption = 'normal' | 'left' | 'right';
export type LabelValueAlignment = 'horizontal' | 'vertical' | 'vertical_stacked';
export type BlockNameAlignment = 'left' | 'center' | 'right';
export type BlockNamePlacement = 'vertical' | 'horizontal_left' | 'horizontal_right';
export type LtrRtlControl = 'auto' | 'rtl' | 'ltr';

type ApiBlockProperties = ApiBlock['properties'];
type LabelApiStylePropertyKey =
    | 'label_font_name'
    | 'label_font_size'
    | 'label_font_weight'
    | 'label_font_width'
    | 'label_align'
    | 'label_vertical_align'
    | 'label_color';
type ValueApiStylePropertyKey =
    | 'value_font_name'
    | 'value_font_size'
    | 'value_font_weight'
    | 'value_font_width'
    | 'value_align'
    | 'value_vertical_align'
    | 'value_color';

const SHARED_LABEL_VALUE_STYLE_API_PROPERTY_PAIRS: ReadonlyArray<
    readonly [LabelApiStylePropertyKey, ValueApiStylePropertyKey]
> = [
    ['label_font_name', 'value_font_name'],
    ['label_font_size', 'value_font_size'],
    ['label_font_weight', 'value_font_weight'],
    ['label_font_width', 'value_font_width'],
    ['label_vertical_align', 'value_vertical_align'],
    ['label_color', 'value_color'],
] as const;

const SBS_LABEL_VALUE_STYLE_API_PROPERTY_PAIRS: ReadonlyArray<
    readonly [LabelApiStylePropertyKey, ValueApiStylePropertyKey]
> = [
    ...SHARED_LABEL_VALUE_STYLE_API_PROPERTY_PAIRS,
    ['label_align', 'value_align'],
] as const;

function shouldSwapLabelValueAlignment(properties: ApiBlockProperties): boolean {
    return (
        (properties.sbs_blocks === 'left' || properties.sbs_blocks === 'right') &&
        properties.label_value_alignment !== 'vertical' &&
        properties.label_value_alignment !== 'vertical_stacked'
    );
}

function getLabelValueSwappedApiProperties(properties: ApiBlockProperties): ApiBlockProperties {
    const swappedProperties: ApiBlockProperties = { ...properties };
    const styleProperties: Record<LabelApiStylePropertyKey | ValueApiStylePropertyKey, string | number> =
        swappedProperties as Record<LabelApiStylePropertyKey | ValueApiStylePropertyKey, string | number>;
    const stylePropertyPairs: ReadonlyArray<readonly [LabelApiStylePropertyKey, ValueApiStylePropertyKey]> =
        shouldSwapLabelValueAlignment(properties)
            ? SBS_LABEL_VALUE_STYLE_API_PROPERTY_PAIRS
            : SHARED_LABEL_VALUE_STYLE_API_PROPERTY_PAIRS;
    for (const [labelKey, valueKey] of stylePropertyPairs) {
        const currentLabelValue: string | number = styleProperties[labelKey];
        styleProperties[labelKey] = styleProperties[valueKey];
        styleProperties[valueKey] = currentLabelValue;
    }
    return swappedProperties;
}

type BlockDefaults = {
    blockNameDisplayValue: string;
    shouldDisplayBlockName: boolean;
    blockNameFont: string;
    blockNameSize: number;
    blockNameWeight: number;
    blockNameFontWidth: number;
    blockNameColor: string;
    blockNameAlignment: BlockNameAlignment;
    blockNamePlacement: BlockNamePlacement;
    numColumns: number;
    distanceBelowBlockName: number;
    labelValueGap: number;
    marginColumn: number;
    edgeDistance: number;
    distanceBetweenRows: number;
    distanceFromPreviousBlock: number;
    isSongBlock: boolean;
    isSingleCell: boolean;
    isCreditsList: boolean;
    separator: string;
    isLogoBlock: boolean;
    sbsBlocks: SbsBlocksOption;
    sbsDistance: number;
    labelFontName: string;
    labelFontSize: number;
    labelFontWeight: number;
    labelFontWidth: number;
    labelAlign: string;
    labelVerticalAlign: string;
    labelFontColor: string;
    valueFontName: string;
    valueFontSize: number;
    valueFontWeight: number;
    valueFontWidth: number;
    valueAlign: string;
    valueVerticalAlign: string;
    valueFontColor: string;
    hasSeparateValueFormatting: boolean;
    labelValueAlignment: LabelValueAlignment;
    ltrRtlControl: LtrRtlControl;
    exchangeLabelValue: boolean;
    cardFadeInFrames: number;
    cardDurationFullyUpFrames: number;
    cardFadeOutFrames: number;
    cardSlugFrames: number;
    cardOverrideFullWidth: boolean;
    cardVerticalCenter: boolean;
    cardHorizontalOffsetPx: number;
    isLocked: boolean;
    cardTimingLocked: boolean;
    isHidden: boolean;
    dataRows: RowData[];
};

export class Block {
    static readonly CARD_HORIZONTAL_OFFSET_MIN_PX: number = -2048;
    static readonly CARD_HORIZONTAL_OFFSET_MAX_PX: number = 2048;

    static readonly defaults: BlockDefaults = {
        blockNameDisplayValue: '',
        shouldDisplayBlockName: true,
        blockNameFont: 'Roboto[wdth,wght]',
        blockNameSize: 30,
        blockNameWeight: 400,
        blockNameFontWidth: 100,
        blockNameColor: '#000000',
        blockNameAlignment: 'center',
        blockNamePlacement: 'vertical',
        numColumns: 1,
        distanceBelowBlockName: 10,
        labelValueGap: 5,
        marginColumn: 45,
        edgeDistance: 0,
        distanceBetweenRows: 1,
        distanceFromPreviousBlock: 10,
        isSongBlock: false,
        isSingleCell: false,
        isCreditsList: false,
        separator: '•',
        isLogoBlock: false,
        sbsBlocks: 'normal',
        sbsDistance: 0,
        labelFontName: 'Roboto[wdth,wght]',
        labelFontSize: 11,
        labelFontWeight: 400,
        labelFontWidth: 100,
        labelAlign: 'right',
        labelVerticalAlign: 'bottom',
        labelFontColor: '#000000',
        valueFontName: 'Roboto[wdth,wght]',
        valueFontSize: 11,
        valueFontWeight: 400,
        valueFontWidth: 100,
        valueAlign: 'left',
        valueVerticalAlign: 'bottom',
        valueFontColor: '#000000',
        hasSeparateValueFormatting: false,
        labelValueAlignment: 'horizontal',
        ltrRtlControl: 'auto',
        exchangeLabelValue: false,
        cardFadeInFrames: 0,
        cardDurationFullyUpFrames: 0,
        cardFadeOutFrames: 0,
        cardSlugFrames: 0,
        cardOverrideFullWidth: false,
        cardVerticalCenter: false,
        cardHorizontalOffsetPx: 0,
        isLocked: false,
        cardTimingLocked: false,
        isHidden: false,
        dataRows: [] as RowData[],
    };

    static normalizeLabelValueAlignment(
        value: unknown,
        fallback: LabelValueAlignment = Block.defaults.labelValueAlignment
    ): LabelValueAlignment {
        if (typeof value !== 'string') return fallback;
        const normalized: string = value.trim().toLowerCase();
        if (normalized === 'horizontal') return 'horizontal';
        if (normalized === 'vertical') return 'vertical';
        if (normalized === 'vertical_stacked') return 'vertical_stacked';
        return fallback;
    }

    static normalizeBlockNameAlignment(
        value: unknown,
        fallback: BlockNameAlignment = Block.defaults.blockNameAlignment
    ): BlockNameAlignment {
        if (typeof value !== 'string') return fallback;
        const normalized: string = value.trim().toLowerCase();
        if (normalized === 'left'|| normalized === 'center' || normalized === 'right') {
            return normalized as BlockNameAlignment;
        }
        return fallback;
    }

    static normalizeBlockNamePlacement(
        value: unknown,
        fallback: BlockNamePlacement = Block.defaults.blockNamePlacement
    ): BlockNamePlacement {
        if (typeof value !== 'string') return fallback;
        const normalized: string = value.trim().toLowerCase();
        if (normalized === 'vertical' || normalized === 'horizontal_left' || normalized === 'horizontal_right') {
            return normalized as BlockNamePlacement;
        }
        return fallback;
    }

    static normalizeLtrRtlControl(
        value: unknown,
        fallback: LtrRtlControl = Block.defaults.ltrRtlControl
    ): LtrRtlControl {
        if (typeof value !== 'string') return fallback;
        const normalized: string = value.trim().toLowerCase();
        return normalized === 'rtl' || normalized === 'ltr' || normalized === 'auto'
            ? normalized
            : fallback;
    }

    static normalizeSbsBlocks(
        value: unknown,
        fallback: SbsBlocksOption = Block.defaults.sbsBlocks
    ): SbsBlocksOption {
        if (typeof value !== 'string') return fallback;
        const normalized: string = value.trim().toLowerCase();
        if (normalized === 'normal' || normalized === 'left' || normalized === 'right') {
            return normalized as SbsBlocksOption;
        }
        return fallback;
    }

    static normalizeCardHorizontalOffsetPx(
        value: unknown,
        fallback: number = Block.defaults.cardHorizontalOffsetPx
    ): number {
        const numericValue: number = typeof value === 'number'
            ? value
            : typeof value === 'string' && value.trim() !== ''
                ? Number(value)
                : NaN;

        if (!Number.isFinite(numericValue)) {
            return fallback;
        }

        const integerValue: number = Math.trunc(numericValue);
        return Math.min(
            Block.CARD_HORIZONTAL_OFFSET_MAX_PX,
            Math.max(Block.CARD_HORIZONTAL_OFFSET_MIN_PX, integerValue)
        );
    }

    static create(init: { name: string; id?: string } & Partial<BlockDefaults>): Block {
        const id: string = (init.id && init.id.length > 0) ? init.id : crypto.randomUUID();
        const merged: BlockDefaults = {
            ...Block.defaults,
            ...init,
            blockNameDisplayValue: init.blockNameDisplayValue ?? init.name,
            dataRows: init.dataRows ?? Block.defaults.dataRows,
            sbsBlocks: Block.normalizeSbsBlocks(init.sbsBlocks ?? Block.defaults.sbsBlocks),
            blockNameAlignment: Block.normalizeBlockNameAlignment(init.blockNameAlignment ?? Block.defaults.blockNameAlignment),
            blockNamePlacement: Block.normalizeBlockNamePlacement(init.blockNamePlacement ?? Block.defaults.blockNamePlacement),
            labelValueAlignment: Block.normalizeLabelValueAlignment(init.labelValueAlignment ?? Block.defaults.labelValueAlignment),
            ltrRtlControl: Block.normalizeLtrRtlControl(init.ltrRtlControl ?? Block.defaults.ltrRtlControl),
            cardHorizontalOffsetPx: Block.normalizeCardHorizontalOffsetPx(init.cardHorizontalOffsetPx),
        };
        if (merged.isSingleCell) {
            merged.hasSeparateValueFormatting = true;
            merged.exchangeLabelValue = false;
        }
        return new Block(
            init.name,
            merged.blockNameDisplayValue,
            merged.shouldDisplayBlockName,
            merged.blockNameFont,
            merged.blockNameSize,
            merged.blockNameWeight,
            merged.blockNameFontWidth,
            merged.blockNameColor,
            merged.blockNameAlignment,
            merged.blockNamePlacement,
            merged.numColumns,
            merged.distanceBelowBlockName,
            merged.labelValueGap,
            merged.marginColumn,
            merged.edgeDistance,
            merged.distanceBetweenRows,
            merged.distanceFromPreviousBlock,
            merged.isSongBlock,
            merged.isSingleCell,
            merged.isCreditsList,
            merged.separator,
            merged.isLogoBlock,
            merged.sbsBlocks,
            merged.sbsDistance,
            merged.labelFontName,
            merged.labelFontSize,
            merged.labelFontWeight,
            merged.labelFontWidth,
            merged.labelAlign,
            merged.labelVerticalAlign,
            merged.labelFontColor,
            merged.valueFontName,
            merged.valueFontSize,
            merged.valueFontWeight,
            merged.valueFontWidth,
            merged.valueAlign,
            merged.valueVerticalAlign,
            merged.valueFontColor,
            merged.hasSeparateValueFormatting,
            merged.labelValueAlignment,
            merged.ltrRtlControl,
            merged.exchangeLabelValue,
            merged.cardFadeInFrames,
            merged.cardDurationFullyUpFrames,
            merged.cardFadeOutFrames,
            merged.cardSlugFrames,
            merged.cardOverrideFullWidth,
            merged.cardVerticalCenter,
            merged.cardHorizontalOffsetPx,
            merged.isLocked,
            merged.cardTimingLocked,
            merged.isHidden,
            merged.dataRows,
            id
        );
    }

    private getApiProps(overrideColors: boolean): ApiBlock['properties'] {
        const properties: ApiBlockProperties = {
            name: this.name,
            block_name_display_value: this.blockNameDisplayValue,
            show_block_name: this.shouldDisplayBlockName,
            block_name_font_style: this.blockNameFont,
            block_name_font_size: this.blockNameSize,
            block_name_font_weight: this.blockNameWeight,
            block_name_font_width: this.blockNameFontWidth,
            block_name_color: overrideColors ? '#000000' : this.blockNameColor,
            block_name_alignment: this.blockNameAlignment,
            block_name_placement: this.blockNamePlacement,
            column_num: this.numColumns,
            distance_below_block_name: this.distanceBelowBlockName,
            distance_between_columns: this.marginColumn,
            value_label_gap: this.labelValueGap,
            distance_between_rows: this.distanceBetweenRows,
            distance_from_previous: this.distanceFromPreviousBlock,
            song_block: this.isSongBlock,
            single_cell: this.isSingleCell,
            credits_list: this.isCreditsList,
            credits_list_separator: this.separator,
            logo_block: this.isLogoBlock,
            sbs_blocks: this.sbsBlocks,
            sbs_distance: this.sbsDistance,
            label_font_name: this.labelFontName,
            label_font_size: this.labelFontSize,
            label_font_weight: this.labelFontWeight,
            label_font_width: this.labelFontWidth,
            label_align: this.labelAlign,
            label_vertical_align: this.labelVerticalAlign,
            label_color: overrideColors ? '#000000' : this.labelFontColor,
            value_font_name: this.valueFontName,
            value_font_size: this.valueFontSize,
            value_font_weight: this.valueFontWeight,
            value_font_width: this.valueFontWidth,
            value_align: this.valueAlign,
            value_vertical_align: this.valueVerticalAlign,
            value_color: overrideColors ? '#000000' : this.valueFontColor,
            has_separate_value_formatting: this.hasSeparateValueFormatting,
            label_value_alignment: this.labelValueAlignment,
            ltr_rtl_control: this.ltrRtlControl,
            card_fade_in_frames: this.cardFadeInFrames,
            card_duration_fully_up_frames: this.cardDurationFullyUpFrames,
            card_fade_out_frames: this.cardFadeOutFrames,
            card_slug_frames: this.cardSlugFrames,
            card_override_full_width: this.cardOverrideFullWidth,
            card_vertical_center: this.cardVerticalCenter,
            card_horizontal_offset_px: this.cardHorizontalOffsetPx,
            block_locked: this.isLocked,
            margin_column: this.marginColumn,
            edge_distance: this.edgeDistance,
            label_value_gap: this.labelValueGap,
        };
        if (!this.exchangeLabelValue) {
            return properties;
        }
        return getLabelValueSwappedApiProperties(properties);
    }

    toRenderApiProperties(overrideColors: boolean = false): ApiBlock['properties'] {
        return this.getApiProps(overrideColors);
    }

    toPreviewApiProperties(): ApiBlock['properties'] {
        return this.getApiProps(false);
    }

    constructor(
        public name: string,
        public blockNameDisplayValue: string,
        public shouldDisplayBlockName: boolean,
        public blockNameFont: string,
        public blockNameSize: number,
        public blockNameWeight: number,
        public blockNameFontWidth: number,
        public blockNameColor: string,
        public blockNameAlignment: BlockNameAlignment,
        public blockNamePlacement: BlockNamePlacement,
        public numColumns: number,
        public distanceBelowBlockName: number,
        public labelValueGap: number,
        public marginColumn: number,
        public edgeDistance: number,
        public distanceBetweenRows: number,
        public distanceFromPreviousBlock: number,
        public isSongBlock: boolean,
        public isSingleCell: boolean,
        public isCreditsList: boolean,
        public separator: string,
        public isLogoBlock: boolean,
        public sbsBlocks: SbsBlocksOption,
        public sbsDistance: number,
        public labelFontName: string,
        public labelFontSize: number,
        public labelFontWeight: number,
        public labelFontWidth: number,
        public labelAlign: string,
        public labelVerticalAlign: string,
        public labelFontColor: string,
        public valueFontName: string,
        public valueFontSize: number,
        public valueFontWeight: number,
        public valueFontWidth: number,
        public valueAlign: string,
        public valueVerticalAlign: string,
        public valueFontColor: string,
        public hasSeparateValueFormatting: boolean = false,
        public labelValueAlignment: LabelValueAlignment = 'horizontal',
        public ltrRtlControl: LtrRtlControl = 'auto',
        public exchangeLabelValue: boolean = false,
        public cardFadeInFrames: number = 0,
        public cardDurationFullyUpFrames: number = 0,
        public cardFadeOutFrames: number = 0,
        public cardSlugFrames: number = 0,
        public cardOverrideFullWidth: boolean = false,
        public cardVerticalCenter: boolean = false,
        public cardHorizontalOffsetPx: number = 0,
        public isLocked: boolean = false,
        public cardTimingLocked: boolean = false,
        public isHidden: boolean = false,
        public dataRows: RowData[] = [],
        public readonly id: string = ''
    ) {}
}
